import { useEffect, useState } from "react";
import { findTimeZone } from "timezone-support";
import { getIanaTimezones } from "../../Steps/Availability/TimezoneSelector/utils/getIanaTimezones/getIanaTimezones";

/**
 * URL of Appointedd's Geo IP service.
 */
export const GEO_IP_SERVICE_URL = "https://u9ii5aumh4.execute-api.eu-west-1.amazonaws.com/prod/geoip";
type Timezone = string;
export type ClientLocationHookOptions = {
  /**
   * Location to fallback to if all other attempts to resolve the
   * client's location fails.
   */
  fallbackLocation: string;
  /**
   * IANA time zone to fallback to if all other attempts to resolve the
   * client's time zone fails.
   */
  fallbackTimezone: string;
  /**
   * Optional flag to force the hook to resolve the client's time zone with
   * Appointedd's Geo IP service, regardless of if the browser's Intl time zone
   * resolution was successful.
   */
  forceGeoIPTimezoneResolver?: boolean;
  /**
   * Optional IANA time zone to force the hook to resolve to this time zone,
   * bypassing any other resolution logic.
   */
  overrideTimezone?: string;
};

/**
 * Custom hook that resolves the current client's timezone and physical location
 *
 * First attempts to resolve location data from Appointedd's GeoIP service.
 *
 * Returned timezone first attempts to use the browser's Intl.DateTimeFormat's
 * computed time zone if the browser supports it. If not supported, it will
 * attempt to use the GeoIP service timezone. If that also fails then use the
 * fallback time zone value that is passed into this hook (defaulting to
 * `Europe/London` if not set).
 *
 *
 * @example
 * ```
 * const MyComponent = () => {
 *   const clientTimezone = useClientTimezone();
 *
 *   // ...
 * }
 * ```
 *
 * @returns An object with client's location and timezone tracked as React
 * state variables. This is asynchronously updated with the result of the Geo
 * IP resolver (timezone is not updated if Intl.DateTimeFormat resolution was
 * successful).
 */
export const useClientLocation = ({
  fallbackLocation,
  fallbackTimezone,
  forceGeoIPTimezoneResolver = false,
  overrideTimezone
}: ClientLocationHookOptions): {
  location?: string;
  timezone: Timezone;
} => {
  const [clientTimezone, setClientTimezone] = useState<Timezone>(fallbackTimezone);
  const [clientLocation, setClientLocation] = useState<string | undefined>();
  const tryResolveIntlTimezone = (): Error | Timezone => {
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      /**
       * Ensure Intl API returns a timezone code that we can handle by
       * searching for it in our list of supported timezones. Throw an
       * error if its not found. Throwing here will cause another
       * TZ detection method to be used.
       */
      const validTimezones = getIanaTimezones();
      if (validTimezones.findIndex(tz => tz === timezone) === -1) {
        throw new Error("Unable to detect valid timezone");
      }
      return timezone;
    } catch (error) {
      if (error instanceof Error) {
        return error;
      }

      // Ignoring this as Mock throwing an Error that is not an instance
      // of an error does not seem worth it.
      /* istanbul ignore next */
      throw error;
    }
  };

  /**
   * Attempt to resolve the client's location data by making a call to
   * Appointedd's GeoIP service and returns an error if it fails rather than
   * throwing a runtime exception.
   */
  const tryResolveLocationDataFromGeoIPService = async (): Promise<{
    country: Error | string;
    timezone: Error | Timezone;
  }> => {
    type GeoIpResponse = {
      country: string;
      timezone: string;
    };
    try {
      const response = await fetch(GEO_IP_SERVICE_URL);
      const data = (await response.json() as GeoIpResponse);
      if (!data) {
        return {
          country: new Error(`Expected 'country' property on response object, Geo IP service returned ${data}`),
          timezone: new Error(`Expected 'timezone' property on response object from Geo IP service, Geo IP service returned ${data}`)
        };
      }
      const country = data.country ?? new Error("Expected 'country' property on response object from Geo IP service");
      const timezone = data.timezone ??
      /**
       * This is not tested as there is no way to meaningfully test it.
       * If an error is thrown the currently selected timezone does not change
       * We could have a test that hits this code but we would only be doing
       * that for the sake of line coverage stats.
       */
      /* istanbul ignore next */
      new Error("Expected 'timezone' property on response object from Geo IP service");

      /**
       * Check if the time zone is actually valid (`findTimeZone()` throws an
       * error if it isn't).
       */
      if (typeof timezone === "string") findTimeZone(timezone);
      return {
        country,
        timezone
      };
    } catch (error) {
      return {
        country: new Error(`Expected 'country' property on response object, Geo IP service returned ${error}`),
        timezone: new Error(`Expected 'timezone' property on response object from Geo IP service, Geo IP service returned ${error}`)
      };
    }
  };
  useEffect(() => {
    const resolveLocationData = async () => {
      const {
        country,
        timezone
      } = await tryResolveLocationDataFromGeoIPService();
      if (!(country instanceof Error)) {
        setClientLocation(country); // Set the location if valid
      } else {
        setClientLocation(fallbackLocation); // Set fallback if there is an error
      }
      if (overrideTimezone) {
        setClientTimezone(overrideTimezone);
      } else {
        let newTimezone: Timezone | undefined = undefined;
        if (!(timezone instanceof Error)) {
          newTimezone = timezone;
        }
        if (!forceGeoIPTimezoneResolver) {
          const intlTimezone = tryResolveIntlTimezone();
          if (!(intlTimezone instanceof Error)) {
            newTimezone = intlTimezone;
          }
        }
        if (newTimezone !== undefined) {
          setClientTimezone(newTimezone);
        }
      }
    };
    void resolveLocationData();
  }, [fallbackLocation, fallbackTimezone, forceGeoIPTimezoneResolver, overrideTimezone]);
  return {
    location: clientLocation,
    timezone: clientTimezone
  };
};
export default useClientLocation;